import { supabase } from '@/integrations/supabase/client';

export interface PageSEO {
  title: string;
  description: string;
  keywords: string | null;
  og_image: string | null;
}

export const PAGE_SEO_UPDATED_EVENT = 'page-seo-settings:updated';

const pageSEOCache = new Map<string, PageSEO | null>();

export function normalizePagePath(input: string): string {
  if (!input) return '/';

  let path = input;

  // Allow passing full URLs
  try {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      path = new URL(path).pathname;
    }
  } catch {
    // ignore
  }

  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

  return path;
}

export function hasCachedPageSEO(pagePath: string): boolean {
  return pageSEOCache.has(normalizePagePath(pagePath));
}

export function getCachedPageSEO(pagePath: string): PageSEO | null | undefined {
  return pageSEOCache.get(normalizePagePath(pagePath));
}

export async function loadPageSEO(pagePath: string): Promise<PageSEO | null> {
  const normalized = normalizePagePath(pagePath);

  const { data } = await supabase
    .from('page_seo_settings')
    .select('title, description, keywords, og_image')
    .eq('page_path', normalized)
    .maybeSingle();

  const seo = (data as PageSEO | null) ?? null;
  pageSEOCache.set(normalized, seo);

  return seo;
}

export function clearPageSEOCache(pagePath?: string) {
  if (pagePath) {
    pageSEOCache.delete(normalizePagePath(pagePath));
  } else {
    pageSEOCache.clear();
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(PAGE_SEO_UPDATED_EVENT, {
        detail: { pagePath: pagePath ? normalizePagePath(pagePath) : undefined },
      })
    );
  }
}
